import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { navigateBack } from '../router';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const SKILLS = [
  { icon: '🔍', title: 'Análisis profundo', desc: 'Lee tu producto o idea y extrae lo que importa: a quién va, qué dolor resuelve, qué lo hace diferente.' },
  { icon: '🎯', title: 'Estrategia de contenido', desc: 'Decide el ángulo desde el que hay que hablar para que el mensaje llegue, no para que suene bonito.' },
  { icon: '🔑', title: 'Keywords con cabeza', desc: 'Identifica las palabras clave que tiene sentido usar — las que busca tu audiencia, no las que te gustan a ti.' },
  { icon: '🗣️', title: 'Tono y voz', desc: 'Define cómo tiene que sonar el contenido para ser coherente con tu marca, aunque no hayas escrito un manual de estilo.' },
  { icon: '✅', title: 'Revisión final', desc: 'Cuando todos terminan, el Director vuelve a revisar que el resultado encaja con el brief que él mismo repartió.' },
];

@customElement('agent-director-page')
export class AgentDirectorPage extends LitElement {
  private goBack() {
    navigateBack('/arquitectura');
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .back-btn {
        margin-bottom: var(--space-xl);
      }

      .agent-header {
        margin-bottom: var(--space-2xl);
      }

      .agent-emoji {
        font-size: 3rem;
        line-height: 1;
        margin-bottom: var(--space-md);
        display: block;
      }

      .agent-title {
        font-size: clamp(2rem, 5vw, 2.75rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .agent-tagline {
        font-size: 1.125rem;
        color: var(--color-accent);
        font-weight: 500;
        margin: 0;
      }

      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .what-section {
        margin-bottom: var(--space-2xl);
      }

      .what-section p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.7;
        margin: 0;
        max-width: 64ch;
      }

      .skills-grid {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
        margin-bottom: var(--space-2xl);
      }

      @media (min-width: 640px) {
        .skills-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .skill-card {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .skill-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
        line-height: 1;
        margin-top: 0.1rem;
      }

      .skill-card h3 {
        margin: 0 0 0.25rem;
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
      }

      .skill-card p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .flow-section {
        margin-bottom: var(--space-2xl);
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
        font-weight: 600;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .model-note {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
        margin-bottom: var(--space-xl);
      }

      .model-note strong {
        color: var(--color-text);
      }
    `,
  ];

  render() {
    return html`
      <div class="animate-fade-up">
        <div class="back-btn">
          <ui-button variant="ghost" @ui-click=${this.goBack}>← Volver</ui-button>
        </div>

        <div class="agent-header">
          <span class="agent-emoji">👔</span>
          <h1 class="agent-title">El Director</h1>
          <p class="agent-tagline">El estratega que no toca el teclado — pero sin él, nadie sabe qué escribir.</p>
        </div>

        <div class="what-section">
          <h2 class="section-title">¿Qué hace?</h2>
          <p>
            El Director es el primero en ponerse a trabajar cuando le das un tema o una URL.
            No escribe ni una coma — su trabajo es entender el contexto, decidir el ángulo
            correcto y dar instrucciones precisas al resto del equipo. Sin él, cada agente
            tiraría por su lado. Con él, todos saben exactamente qué hacer y para quién.
            Al final del proceso, vuelve a revisar que el resultado encaje con lo que planificó
            desde el principio.
          </p>
        </div>

        <div>
          <h2 class="section-title">Sus armas</h2>
          <div class="skills-grid">
            ${SKILLS.map(
              (s) => html`
                <article class="card skill-card">
                  <span class="skill-icon">${s.icon}</span>
                  <div>
                    <h3>${s.title}</h3>
                    <p>${s.desc}</p>
                  </div>
                </article>
              `,
            )}
          </div>
        </div>

        <div class="flow-section">
          <h2 class="section-title">Cómo actúa</h2>
          <div class="flow-steps">
            <div class="step">
              <span class="step-num">1</span>
              <div>
                <h3>Recibe la chispa</h3>
                <p>Una URL o una descripción es suficiente. El Director extrae todo lo que puede: qué es el producto, a quién va dirigido, qué lo hace diferente.</p>
              </div>
            </div>
            <div class="step">
              <span class="step-num">2</span>
              <div>
                <h3>Define el plan</h3>
                <p>Establece el tono, el ángulo narrativo y las palabras clave. Esto se convierte en el "brief" que todos los demás agentes reciben para hacer su trabajo.</p>
              </div>
            </div>
            <div class="step">
              <span class="step-num">3</span>
              <div>
                <h3>Revisa y cierra</h3>
                <p>Cuando el Secretario termina su auditoría, el Director hace una última pasada. Si todo está bien, genera el prompt para la imagen y da el visto bueno final.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="model-note">
          <strong>Modelos: GLM 5.2 y Llama 3.3-70B</strong> — El Director combina la visión estratégica de GLM 5.2 con la capacidad creativa y contextual de Llama 3.3-70B para tomar decisiones de alto nivel. GLM 5.2 destaca por su planificación, análisis profundo y talento para descomponer problemas y establecer rutas claras de acción; mientras que Llama 3.3-70B refuerza la comprensión de matices, aporta riqueza narrativa y asegura la coherencia en la ejecución. Juntos trabajan de forma complementaria, identificando oportunidades, anticipando objeciones, alineando tono y mensaje y convirtiendo información dispersa en estrategias de contenido precisas, listas para que el equipo las lleve a la práctica.
        </div>

        <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Probar Blunticle</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agent-director-page': AgentDirectorPage;
  }
}
